"use client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { atom, useAtom } from "jotai";
import { toast } from "sonner";
import {
  OrganizationSummary,
  Prompt,
  Tag,
  TagType,
} from "@constructr-sdk/entities";
import { trpcReactQueryApi } from "~/trpc/react";
import { authClient } from "@constructr-sdk/auth/client";
import type {
  UseExploreParams,
  UseExploreReturn,
} from "~/modules/main/hooks/use-explore/use-explore.interface";

// Global state atoms - hoisted to top level to prevent recreation
const activeFiltersAtom = atom<Record<TagType, string[]>>({
  [TagType.CATEGORY]: [],
  [TagType.USE_CASE]: [],
  [TagType.ROLE]: [],
  [TagType.INDUSTRY]: [],
});

const stagedFiltersAtom = atom<Record<TagType, string[]>>({
  [TagType.CATEGORY]: [],
  [TagType.USE_CASE]: [],
  [TagType.ROLE]: [],
  [TagType.INDUSTRY]: [],
});

const searchQueryAtom = atom<string>("");
const selectedPromptAtom = atom<Prompt | null>(null);
const isDetailsOpenAtom = atom<boolean>(false);
const currentPageAtom = atom<number>(1);

/**
 * Custom hook for the Explore view to browse published prompts.
 * Implements search, filtering, pagination, and details sidebar functionality.
 */
export function useExplore(params: UseExploreParams): UseExploreReturn {
  // Global state
  const [activeFilters, setActiveFilters] = useAtom(activeFiltersAtom);
  const [stagedFilters, setStagedFilters] = useAtom(stagedFiltersAtom);
  const [searchQuery, setSearchQueryState] = useAtom(searchQueryAtom);
  const [selectedPrompt, setSelectedPrompt] = useAtom(selectedPromptAtom);
  const [isDetailsOpen, setIsDetailsOpen] = useAtom(isDetailsOpenAtom);
  const [currentPage, setCurrentPageState] = useAtom(currentPageAtom);

  // Active organization bootstrap to prevent 401 errors
  const { data: activeOrg } = authClient.useActiveOrganization();
  const { data: orgs } = authClient.useListOrganizations();
  const didSetActiveRef = useRef<boolean>(false);

  // Set default active organization if none is set
  useEffect(() => {
    if (didSetActiveRef.current) return;
    if (!activeOrg?.id && (orgs?.length ?? 0) > 0) {
      didSetActiveRef.current = true;
      void authClient.organization.setActive({ organizationId: orgs![0]!.id });
    }
  }, [activeOrg?.id, orgs]);

  // TRPC utils for cache invalidation
  const utils = trpcReactQueryApi.useUtils();

  // Fetch published prompts with current filters and search
  const {
    data: promptsData,
    isLoading: isLoadingPrompts,
    isError: isPromptsError,
  } = trpcReactQueryApi.main.explore.publishedPrompts.useQuery({
    page: currentPage,
    pageSize: 12,
    searchQuery: searchQuery || undefined,
    tagFilters: activeFilters,
  });

  // Fetch all tags grouped by type
  const {
    data: tagsByTypeData,
    isLoading: isLoadingTags,
    isError: isTagsError,
  } = trpcReactQueryApi.main.explore.allTagsByType.useQuery(undefined);

  // Fetch user organizations for copy-to-library
  const {
    data: organizationsData,
    isLoading: isLoadingOrganizations,
    isError: isOrganizationsError,
  } = trpcReactQueryApi.main.explore.userOrganizations.useQuery(undefined);

  // Copy prompt to library mutation
  const copyToLibraryMutation =
    trpcReactQueryApi.main.explore.copyToLibrary.useMutation({
      onSuccess: async () => {
        toast.success("Prompt copied to library successfully");
        await utils.main.prompts.all.invalidate();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to copy prompt to library");
      },
    });

  // Derived state
  const prompts = promptsData?.prompts ?? [];
  const totalPages = promptsData?.totalPages ?? 1;
  const tagsByType = tagsByTypeData ?? {
    [TagType.CATEGORY]: [],
    [TagType.USE_CASE]: [],
    [TagType.ROLE]: [],
    [TagType.INDUSTRY]: [],
  };
  const organizations = organizationsData ?? [];

  // Build tags dictionary for efficient lookup
  const tagsDictionary = useMemo(() => {
    const dict: Record<string, Tag> = {};
    Object.values(tagsByType).forEach((tags) => {
      tags.forEach((tag) => {
        dict[tag.id] = tag;
      });
    });
    return dict;
  }, [tagsByType]);

  // Parse dynamic variables from selected prompt messages
  const selectedPromptVariables = useMemo(() => {
    if (!selectedPrompt) return [];

    const variableSet = new Set<string>();
    const variableRegex = /\{\{([^}]+)\}\}/g;

    selectedPrompt.messages.forEach((message) => {
      let match;
      while ((match = variableRegex.exec(message.content)) !== null) {
        variableSet.add(match[1]!.trim());
      }
    });

    return Array.from(variableSet);
  }, [selectedPrompt]);

  // Loading and error states
  const isLoading = isLoadingPrompts || isLoadingTags || isLoadingOrganizations;
  const loadError = isPromptsError
    ? "Failed to load prompts"
    : isTagsError
      ? "Failed to load tags"
      : isOrganizationsError
        ? "Failed to load organizations"
        : null;

  // Mutations
  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryState(query);
      // Reset to page 1 when search changes
      setCurrentPageState(1);
    },
    [setSearchQueryState, setCurrentPageState],
  );

  const toggleStagedFilter = useCallback(
    (type: TagType, tagId: string) => {
      setStagedFilters((prev) => {
        const currentFilters = prev[type] ?? [];
        const isPresent = currentFilters.includes(tagId);

        return {
          ...prev,
          [type]: isPresent
            ? currentFilters.filter((id) => id !== tagId)
            : [...currentFilters, tagId],
        };
      });
    },
    [setStagedFilters],
  );

  const removeActiveFilter = useCallback(
    (type: TagType, tagId: string) => {
      setActiveFilters((prev) => {
        const currentFilters = prev[type] ?? [];
        return {
          ...prev,
          [type]: currentFilters.filter((id) => id !== tagId),
        };
      });
      // Also update staged filters to match
      setStagedFilters((prev) => {
        const currentFilters = prev[type] ?? [];
        return {
          ...prev,
          [type]: currentFilters.filter((id) => id !== tagId),
        };
      });
      // Reset to page 1 when filters change
      setCurrentPageState(1);
    },
    [setActiveFilters, setStagedFilters, setCurrentPageState],
  );

  const clearAllFilters = useCallback(() => {
    const emptyFilters = {
      [TagType.CATEGORY]: [],
      [TagType.USE_CASE]: [],
      [TagType.ROLE]: [],
      [TagType.INDUSTRY]: [],
    };
    setActiveFilters(emptyFilters);
    setStagedFilters(emptyFilters);
    setCurrentPageState(1);
  }, [setActiveFilters, setStagedFilters, setCurrentPageState]);

  const setCurrentPage = useCallback(
    (page: number) => {
      setCurrentPageState(page);
    },
    [setCurrentPageState],
  );

  const selectPrompt = useCallback(
    (prompt: Prompt) => {
      setSelectedPrompt(prompt);
    },
    [setSelectedPrompt],
  );

  // Commands
  const initialize = useCallback(async () => {
    // Initialize is handled by the useQuery hooks automatically
    // This function is idempotent and safe to call multiple times
    // The queries will only refetch if needed
  }, []);

  const applyFilters = useCallback(async () => {
    // Copy staged filters to active filters
    setActiveFilters(stagedFilters);
    // Reset to page 1
    setCurrentPageState(1);
    // The useQuery hook will automatically refetch with new filters
  }, [stagedFilters, setActiveFilters, setCurrentPageState]);

  const resetFilters = useCallback(() => {
    // Reset staged filters to match active filters
    setStagedFilters(activeFilters);
  }, [activeFilters, setStagedFilters]);

  const openPromptDetails = useCallback(
    async (promptId: string) => {
      try {
        // Check if prompt is already in memory
        const promptInMemory = prompts.find((p) => p.id === promptId);

        if (promptInMemory) {
          setSelectedPrompt(promptInMemory);
          setIsDetailsOpen(true);
        } else {
          // Fetch the full prompt from the server
          const prompt =
            await utils.main.explore.promptById.fetch({ promptId });
          setSelectedPrompt(prompt);
          setIsDetailsOpen(true);
        }
      } catch (error) {
        toast.error("Failed to load prompt details");
        console.error("Error loading prompt details:", error);
      }
    },
    [prompts, setSelectedPrompt, setIsDetailsOpen, utils],
  );

  const closePromptDetails = useCallback(() => {
    setIsDetailsOpen(false);
    // Clear selection after a brief delay to allow for close animation
    setTimeout(() => {
      setSelectedPrompt(null);
    }, 300);
  }, [setIsDetailsOpen, setSelectedPrompt]);

  const copyPromptToLibrary = useCallback(
    async (destinationOrganizationId: string) => {
      if (!selectedPrompt) {
        throw new Error("No prompt selected");
      }

      const result = await copyToLibraryMutation.mutateAsync({
        promptId: selectedPrompt.id,
        destinationOrganizationId,
      });

      return { newPromptId: result.newPromptId };
    },
    [selectedPrompt, copyToLibraryMutation],
  );

  const buildConcatenatedPromptForSelected = useCallback(() => {
    if (!selectedPrompt) {
      return "";
    }

    const parts: string[] = [];

    selectedPrompt.messages.forEach((message) => {
      const roleLabel = message.role.toUpperCase();
      parts.push(`${roleLabel}:\n${message.content}`);
    });

    return parts.join("\n\n");
  }, [selectedPrompt]);

  return {
    // State
    prompts,
    tagsByType,
    tagsDictionary,
    activeFilters,
    stagedFilters,
    searchQuery,
    selectedPrompt,
    selectedPromptVariables,
    isDetailsOpen,
    organizations,
    isLoading,
    loadError,
    currentPage,
    totalPages,

    // Mutations
    setSearchQuery,
    toggleStagedFilter,
    removeActiveFilter,
    clearAllFilters,
    setCurrentPage,
    selectPrompt,

    // Commands
    initialize,
    applyFilters,
    resetFilters,
    openPromptDetails,
    closePromptDetails,
    copyPromptToLibrary,
    buildConcatenatedPromptForSelected,
  };
}